import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox, Input } from '@/components/ui'
import { Pencil, Plus } from 'lucide-react'
import { MemoriesCreateModal } from './MemoriesCreateModal'
import { MemoriesCustomCoverModal } from './MemoriesCustomCover'
import Loader from '../../../common/components/Loader'
import { Languages, Memory } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import {
  getAllMemories,
  getMemory,
  publishMemory,
  unpublishMemory,
} from '../../../core/services/memories'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'

export const MemoriesPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()

  const { hasEditPermission } = useCheckPermission({ page: 'MEMORIES' })

  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [showCreateMemoriesModal, setShowCreateMemoriesModal] =
    useState<boolean>(false)
  const [showCustomCoverModal, setShowCustomCoverModal] =
    useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentMemory, setCurrentMemory] = useState<Memory>()
  const [memories, setMemories] = useState<Memory[]>([])
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        if (allLanguagesResponse.status === 200) {
          setAllLanguages(allLanguagesResponse.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const memoriesResponse = await getAllMemories({
        page,
        size,
        sortBy,
        sortDirection,
      })
      if (memoriesResponse.status === 200) {
        setTotalElemenets(memoriesResponse.data.totalElements)
        setMemories(memoriesResponse.data.content)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, editionId, festivalName, sortBy, sortDirection])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  useEffect(() => {
    getData()
  }, [getData])

  const handlePublish = (id: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await publishMemory(id)
        getData()
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (id: string[]) => {
    // prettier-ignore
    (async () => {
      if (!hasEditPermission) {
        return
      }
      try {
        setLoading(true)
        const { status } = await unpublishMemory(id)
        getData();
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const columns: IColumn[] = [
    {
      title: 'Title',
      accessor: 'title',
      render: (row) => (
        <div className="min-w-[160px]">
          <span>{row.title}</span>
        </div>
      ),
    },
    {
      title: 'Image (630x360)',
      accessor: '',
      render: (row) => (
        <img
          src={row.imageLink}
          className="rounded-[10px] h-[100px] w-[180px] object-contain"
        />
      ),
    },
    {
      title: 'Description',
      accessor: 'description',
      render: (row) => {
        const plainText = row.description?.replace(/<[^>]*>/g, '') || ''
        const isExpanded = Boolean(expandedRows[row.id])
        const hasLongDescription = plainText.length > 220
        return (
          <div className="bg-white/10 p-2 rounded-2xl min-w-[260px]">
            <div className={isExpanded ? '' : 'line-clamp-3'}>{plainText}</div>
            {hasLongDescription && (
              <span
                onClick={() => {
                  setExpandedRows((prev) => ({
                    ...prev,
                    [row.id]: !prev[row.id],
                  }))
                }}
                className="text-accent-1 cursor-pointer inline-block mt-1"
              >
                {isExpanded ? '[show less]' : '[...]'}
              </span>
            )}
          </div>
        )
      },
    },
    {
      title: 'Published Date',
      accessor: 'publishedDate',

      render: (row) =>
        row.publishDate
          ? formatDateTime(new Date(row.publishDate).toISOString())
          : '-',
    },
    {
      title: 'Ordinal',
      accessor: 'ordinal',
      sortable: true,
      render: (row) => (
        <Input
          value={row.ordinal}
          readOnly
          className="bg-monochrome-4 border-none"
        />
      ),
    },
    {
      title: 'Published',
      accessor: '',
      render: (row) => {
        return (
          <Checkbox
            onCheckedChange={() => {
              if (row.published) {
                handleUnpublish([row.id])
              } else {
                handlePublish([row.id])
              }
            }}
            checked={row.published}
            disabled={!hasEditPermission}
          />
        )
      },
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={async (event) => {
            event.preventDefault()
            if (!hasEditPermission) {
              return
            }
            try {
              const response = await getMemory(row.id)
              setCurrentMemory(response.data)
              setShowCreateMemoriesModal(true)
              setEditMode(true)
            } catch (e) {
              console.error(e)
            }
          }}
        >
          Edit
        </a>
      ),
      width: '80px',
    },
  ]

  return (
    <>
      <Header
        pageTitle="Memories"
        actions={
          <>
            {hasEditPermission && (
              <>
                <Button
                  variant="secondary"
                  className="border border-monochrome-120"
                  type="button"
                  onClick={() => {
                    setShowCustomCoverModal(true)
                  }}
                >
                  Customise Cover Image
                  <Icon
                    icon="edit-square"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
                <Button
                  variant="primary-bordered"
                  onClick={() => {
                    setEditMode(false)
                    setShowCreateMemoriesModal(true)
                    setCurrentMemory(undefined)
                  }}
                >
                  Add New
                  <Icon
                    icon="plus-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Memories" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={memories}
          columnWidth="20%"
          handleSortBy={handleSortBy}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <MemoriesCustomCoverModal
        setShowCreateModal={setShowCustomCoverModal}
        showCreateModal={showCustomCoverModal}
        allLanguages={allLanguages}
      />
      <MemoriesCreateModal
        setEditMode={setEditMode}
        setShowCreateModal={setShowCreateMemoriesModal}
        memory={currentMemory}
        setMemories={setMemories}
        showCreateModal={showCreateMemoriesModal}
        editMode={editMode}
        page={page}
        pageChange={pageChange}
        size={size}
        setTotalElemenets={setTotalElemenets}
        allLanguages={allLanguages}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
